import { Box, Button, Card, Divider, Modal, Tag, Text } from '@nimbus-ds/components';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Programacion } from '../api';
import { Dato } from '../componentes/Dato';
import { Cargando, ErrorCaja } from '../componentes/Estado';
import { Layout } from '../componentes/Layout';
import { aErrorVisible, type ErrorVisible } from '../errores';
import { fechaHora } from '../formato';
import { useSesion } from '../sesion';

export function Programaciones(): ReactElement {
  const [programaciones, setProgramaciones] = useState<Programacion[] | null>(null);
  const [error, setError] = useState<ErrorVisible | null>(null);
  /** Borrar no tiene deshacer: se pregunta antes. */
  const [aBorrar, setABorrar] = useState<Programacion | null>(null);
  const [borrando, setBorrando] = useState(false);
  const navigate = useNavigate();
  const sesion = useSesion();
  const bloqueada = sesion !== null && !sesion.acceso.puedeActualizar;

  const cargar = useCallback(async (): Promise<void> => {
    try {
      const { programaciones: lista } = await api.programaciones();
      setProgramaciones(lista);
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos cargar tus programaciones.'));
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function alternar(programacion: Programacion): Promise<void> {
    setError(null);
    try {
      if (programacion.activa) await api.pausarProgramacion(programacion.id);
      else await api.activarProgramacion(programacion.id);
      await cargar();
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos cambiar la programación.'));
    }
  }

  async function borrar(): Promise<void> {
    if (aBorrar === null) return;
    setError(null);
    setBorrando(true);
    try {
      await api.borrarProgramacion(aBorrar.id);
      setABorrar(null);
      await cargar();
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos borrar la programación.'));
    } finally {
      setBorrando(false);
    }
  }

  return (
    <Layout
      titulo="Programaciones"
      acciones={
        <Button
          appearance="primary"
          disabled={bloqueada}
          onClick={() => navigate('/programaciones/nueva')}
        >
          Nueva programación
        </Button>
      }
    >
      {error !== null && <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />}
      {programaciones === null && error === null && <Cargando />}

      {programaciones !== null && programaciones.length === 0 && (
        <Card>
          <Card.Header title="Todavía no programaste ninguna actualización" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap="4" alignItems="flex-start">
              <Text>
                Una programación corre sola: el día y la hora que elijas recalculamos la regla sobre
                los precios de ese momento y actualizamos. Siempre en hora de Buenos Aires.
              </Text>
              <Text fontSize="caption" color="neutral-textLow">
                Sirve para los aumentos que ya sabés que vienen: el primero de cada mes, o
                cada lunes. Podés pausarla o borrarla cuando quieras, y las actualizaciones
                que haga se deshacen igual que las manuales.
              </Text>
              <Box className="acciones-a-lo-ancho" display="flex">
                <Button
                  size="large"
                  appearance="primary"
                  disabled={bloqueada}
                  onClick={() => navigate('/programaciones/nueva')}
                >
                  Crear la primera
                </Button>
              </Box>
            </Box>
          </Card.Body>
        </Card>
      )}

      {/*
        Tarjetas también en escritorio, por lo mismo que el historial: seis columnas más
        tres botones no entraban y "Borrar" quedaba cortado contra el borde del recuadro.
      */}
      {programaciones !== null && programaciones.length > 0 && (
        <Box display="flex" flexDirection="column" gap={{ xs: '2', md: '3' }}>
          {programaciones.map((programacion) => (
            <TarjetaProgramacion
              key={programacion.id}
              programacion={programacion}
              bloqueada={bloqueada}
              onEditar={() => navigate(`/programaciones/${programacion.id}/editar`)}
              onAlternar={() => void alternar(programacion)}
              onBorrar={() => setABorrar(programacion)}
            />
          ))}
        </Box>
      )}

      <Text fontSize="caption" color="neutral-textLow">
        Antes de cada corrida programada recalculamos sobre los precios de ese día: la regla es
        &quot;+12% sobre lo que valga hoy&quot;, no sobre lo que valía cuando la creaste.
      </Text>

      <Modal open={aBorrar !== null} onDismiss={() => setABorrar(null)}>
        <Modal.Header title="¿Borrar esta programación?" />
        <Modal.Body padding="none">
          <Text>
            {aBorrar === null
              ? ''
              : `"${aBorrar.reglaNombre}" deja de correr sola. Las actualizaciones que ya hizo quedan en el historial y se pueden deshacer igual.`}
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setABorrar(null)}>No, dejala</Button>
          <Button appearance="danger" disabled={borrando} onClick={() => void borrar()}>
            {borrando ? 'Borrando…' : 'Sí, borrar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

/**
 * Una programación, en 375px y en escritorio.
 *
 * *** "Borrar" va aparte y separado. *** Es la única acción de esta pantalla que no tiene
 * vuelta atrás. Un dedo que apunta a "Pausar" y cae dos milímetros abajo no puede terminar
 * abriendo el modal de borrado, y en escritorio el mismo criterio se paga con 16px.
 *
 * Ni "Próxima" ni "Última" dicen "—". Un guión se lee como un dato que falta y acá
 * significan dos cosas distintas: que la programación está pausada, y que todavía no
 * corrió nunca.
 */
function TarjetaProgramacion({
  programacion,
  bloqueada,
  onEditar,
  onAlternar,
  onBorrar,
}: {
  readonly programacion: Programacion;
  readonly bloqueada: boolean;
  readonly onEditar: () => void;
  readonly onAlternar: () => void;
  readonly onBorrar: () => void;
}): ReactElement {
  return (
    <Card>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3">
          <Box display="flex" justifyContent="space-between" gap="2" alignItems="flex-start">
            <Box display="flex" flexDirection="column" gap="0-5">
              <Text fontSize="highlight" fontWeight="medium">
                {programacion.reglaNombre}
              </Text>
              <Text fontSize="caption" color="neutral-textLow">
                {programacion.descripcion}
              </Text>
            </Box>
            <Tag appearance={programacion.activa ? 'success' : 'neutral'}>
              {programacion.activa ? 'Activa' : 'Pausada'}
            </Tag>
          </Box>

          <Divider />

          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            gap={{ xs: '3', md: '4' }}
          >
            <Box display="flex" gap={{ xs: '4', md: '6' }} flexWrap="wrap">
              <Dato etiqueta="Próxima">
                {programacion.activa ? programacion.proximaEjecucionTexto : 'Pausada'}
              </Dato>
              <Dato etiqueta="Última">
                {programacion.ultimaEjecucion === null
                  ? 'Todavía no corrió'
                  : fechaHora(programacion.ultimaEjecucion)}
              </Dato>
            </Box>

            <Box
              className="acciones-a-lo-ancho"
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              gap="2"
            >
              <Button size="large" onClick={onEditar}>
                Editar
              </Button>
              <Button
                size="large"
                disabled={bloqueada && !programacion.activa}
                onClick={onAlternar}
              >
                {programacion.activa ? 'Pausar' : 'Activar'}
              </Button>
              <Box
                className="acciones-a-lo-ancho"
                display="flex"
                paddingTop={{ xs: '2', md: 'none' }}
                paddingLeft={{ xs: 'none', md: '4' }}
              >
                <Button size="large" appearance="danger" onClick={onBorrar}>
                  Borrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}
